import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { User, Building, Mail, Lock, ShieldCheck, Moon, Sun, Bell, Globe, Save, Loader, Trash2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  nl: {
    title: 'Profiel Instellingen',
    loading: 'Profiel laden...',
    // Profile section
    profileInfo: 'Profiel Informatie',
    fullName: 'Volledige Naam',
    companyName: 'Bedrijfsnaam',
    email: 'E-mailadres',
    emailHint: 'E-mailadres kan niet gewijzigd worden',
    saveChanges: 'Wijzigingen Opslaan',
    saving: 'Opslaan...',
    profileSaved: 'Profiel bijgewerkt!',
    profileError: 'Fout bij opslaan profiel',
    // Preferences
    preferences: 'Voorkeuren',
    theme: 'Thema',
    themeHint: 'Kies je interface thema',
    light: 'Licht',
    dark: 'Donker',
    notifications: 'E-mail Notificaties',
    notificationsHint: 'Ontvang updates en meldingen',
    language: 'Standaardtaal',
    languageHint: 'Voorkeurstaal voor de interface',
    savePrefs: 'Voorkeuren Opslaan',
    prefsSaved: 'Voorkeuren opgeslagen!',
    prefsError: 'Fout bij opslaan voorkeuren',
    // Password
    changePassword: 'Wachtwoord Wijzigen',
    currentPassword: 'Huidig Wachtwoord',
    newPassword: 'Nieuw Wachtwoord',
    confirmPassword: 'Bevestig Nieuw Wachtwoord',
    changeBtn: 'Wachtwoord Wijzigen',
    changing: 'Wijzigen...',
    passwordChanged: 'Wachtwoord gewijzigd!',
    passwordMismatch: 'Nieuwe wachtwoorden komen niet overeen',
    twoFactorTitle: 'Tweestapsverificatie',
    twoFactorDesc: 'Ontvang bij het inloggen een beveiligingscode via e-mail.',
    twoFactorEnabled: 'Aan',
    twoFactorDisabled: 'Uit',
    twoFactorPassword: 'Huidig wachtwoord ter bevestiging',
    twoFactorSave: '2FA-instelling opslaan',
    twoFactorSaved: 'Tweestapsverificatie bijgewerkt.',
    twoFactorError: 'Tweestapsverificatie kon niet worden bijgewerkt.',
    // Delete
    deleteTitle: 'Account Verwijderen',
    deleteDesc: 'Hiermee wordt je account permanent verwijderd, inclusief alle bedrijfsdata, documenten, analyses en je AI-assistent Emma. Dit kan niet ongedaan worden gemaakt.',
    deleteBtn: 'Account Verwijderen',
    deleteConfirmTitle: 'Account Verwijderen?',
    deleteConfirmMsg: 'Weet je zeker dat je je account wilt verwijderen? <strong>Alle data</strong> inclusief je bedrijfsprofiel, documenten, analyses en je Agentic AI-assistent <strong>Emma</strong> worden permanent verwijderd. Dit kan niet ongedaan worden gemaakt.',
    deleteTypePrompt: 'Typ <strong>VERWIJDEREN</strong> om te bevestigen:',
    deleteWord: 'VERWIJDEREN',
    cancel: 'Annuleren',
    deleting: 'Verwijderen...',
    deleteForever: 'Definitief Verwijderen',
    deleteError: 'Er ging iets mis',
  },
  en: {
    title: 'Profile Settings',
    loading: 'Loading profile...',
    profileInfo: 'Profile Information',
    fullName: 'Full Name',
    companyName: 'Company Name',
    email: 'Email Address',
    emailHint: 'Email cannot be changed',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    profileSaved: 'Profile updated!',
    profileError: 'Error updating profile',
    preferences: 'Preferences',
    theme: 'Theme',
    themeHint: 'Choose your interface theme',
    light: 'Light',
    dark: 'Dark',
    notifications: 'Email Notifications',
    notificationsHint: 'Receive updates and alerts',
    language: 'Default Language',
    languageHint: 'Preferred interface language',
    savePrefs: 'Save Preferences',
    prefsSaved: 'Preferences saved!',
    prefsError: 'Error saving preferences',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm New Password',
    changeBtn: 'Change Password',
    changing: 'Changing...',
    passwordChanged: 'Password changed!',
    passwordMismatch: 'New passwords do not match',
    twoFactorTitle: 'Two-factor authentication',
    twoFactorDesc: 'Receive a security code by email when you sign in.',
    twoFactorEnabled: 'On',
    twoFactorDisabled: 'Off',
    twoFactorPassword: 'Current password to confirm',
    twoFactorSave: 'Save 2FA setting',
    twoFactorSaved: 'Two-factor authentication updated.',
    twoFactorError: 'Could not update two-factor authentication.',
    deleteTitle: 'Delete Account',
    deleteDesc: 'This will permanently delete your account, including all business data, documents, analyses and your AI assistant Emma. This cannot be undone.',
    deleteBtn: 'Delete Account',
    deleteConfirmTitle: 'Delete Account?',
    deleteConfirmMsg: 'Are you sure you want to delete your account? <strong>All data</strong> including your company profile, documents, analyses and your AI assistant <strong>Emma</strong> will be permanently deleted. This cannot be undone.',
    deleteTypePrompt: 'Type <strong>DELETE</strong> to confirm:',
    deleteWord: 'DELETE',
    cancel: 'Cancel',
    deleting: 'Deleting...',
    deleteForever: 'Delete Permanently',
    deleteError: 'Something went wrong',
  },
};

const Profile = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang] || T.nl;
  const [profile, setProfile] = useState({ full_name: '', company_name: '' });
  const [preferences, setPreferences] = useState({ theme: 'light', email_notifications: true, default_language: 'en' });
  const [passwordChange, setPasswordChange] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [savedTwoFactorEnabled, setSavedTwoFactorEnabled] = useState(false);
  const [twoFactorPassword, setTwoFactorPassword] = useState('');
  const [savingTwoFactor, setSavingTwoFactor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/`, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(response.data.user);
      setPreferences(response.data.preferences);
      setTwoFactorEnabled(Boolean(response.data.user.two_factor_enabled));
      setSavedTwoFactorEnabled(Boolean(response.data.user.two_factor_enabled));
    } catch (error) { console.error('Error fetching profile:', error); }
    setLoading(false);
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/profile/update`, profile, { headers: { Authorization: `Bearer ${token}` } });
      alert(t.profileSaved);
    } catch { alert(t.profileError); }
    setSaving(false);
  };

  const updatePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile/preferences`, preferences, { headers: { Authorization: `Bearer ${token}` } });
      document.body.classList.toggle('dark-mode', preferences.theme === 'dark');
      alert(t.prefsSaved);
    } catch { alert(t.prefsError); }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordChange.new_password !== passwordChange.confirm_password) { alert(t.passwordMismatch); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/profile/change-password`, { current_password: passwordChange.current_password, new_password: passwordChange.new_password }, { headers: { Authorization: `Bearer ${token}` } });
      alert(t.passwordChanged);
      setPasswordChange({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) { alert(error.response?.data?.detail || t.profileError); }
    setSaving(false);
  };

  const updateTwoFactor = async (e) => {
    e.preventDefault();
    setSavingTwoFactor(true);
    try {
      const response = await axios.put(`${API}/profile/two-factor`, {
        enabled: twoFactorEnabled,
        current_password: twoFactorPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTwoFactorEnabled(response.data.two_factor_enabled);
      setSavedTwoFactorEnabled(response.data.two_factor_enabled);
      setTwoFactorPassword('');
      alert(t.twoFactorSaved);
    } catch (error) {
      setTwoFactorEnabled(savedTwoFactorEnabled);
      alert(error.response?.data?.detail || t.twoFactorError);
    } finally {
      setSavingTwoFactor(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="loader"></div><p>{t.loading}</p></div>;

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="heading-1">{t.title}</h1>
      </div>

      <div className="profile-container">
        {/* Profile Information */}
        <div className="profile-section">
          <h2 className="heading-2">{t.profileInfo}</h2>
          <form onSubmit={updateProfile} className="profile-form">
            <div className="form-group">
              <label className="body-small">{t.fullName}</label>
              <div className="input-with-icon">
                <User size={18} />
                <input type="text" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className="form-input" />
              </div>
            </div>
            <div className="form-group">
              <label className="body-small">{t.companyName}</label>
              <div className="input-with-icon">
                <Building size={18} />
                <input type="text" value={profile.company_name} onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} className="form-input" />
              </div>
            </div>
            <div className="form-group">
              <label className="body-small">{t.email}</label>
              <div className="input-with-icon">
                <Mail size={18} />
                <input type="email" value={profile.email} disabled className="form-input" style={{ opacity: 0.6, cursor: 'not-allowed' }} />
              </div>
              <p className="caption">{t.emailHint}</p>
            </div>
            <button type="submit" disabled={saving} className="btn-primary">
              <Save size={16} /> {saving ? t.saving : t.saveChanges}
            </button>
          </form>
        </div>

        {/* Preferences */}
        <div className="profile-section">
          <h2 className="heading-2">{t.preferences}</h2>
          <div className="preferences-grid">
            <div className="preference-item">
              <div className="preference-info">
                {preferences.theme === 'light' ? <Sun size={20} /> : <Moon size={20} />}
                <div>
                  <p className="body-medium" style={{ fontWeight: 500 }}>{t.theme}</p>
                  <p className="caption">{t.themeHint}</p>
                </div>
              </div>
              <select value={preferences.theme} onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })} className="form-input" style={{ maxWidth: '150px' }}>
                <option value="light">{t.light}</option>
                <option value="dark">{t.dark}</option>
              </select>
            </div>
            <div className="preference-item">
              <div className="preference-info">
                <Bell size={20} />
                <div>
                  <p className="body-medium" style={{ fontWeight: 500 }}>{t.notifications}</p>
                  <p className="caption">{t.notificationsHint}</p>
                </div>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={preferences.email_notifications} onChange={(e) => setPreferences({ ...preferences, email_notifications: e.target.checked })} />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="preference-item">
              <div className="preference-info">
                <Globe size={20} />
                <div>
                  <p className="body-medium" style={{ fontWeight: 500 }}>{t.language}</p>
                  <p className="caption">{t.languageHint}</p>
                </div>
              </div>
              <select value={preferences.default_language} onChange={(e) => setPreferences({ ...preferences, default_language: e.target.value })} className="form-input" style={{ maxWidth: '150px' }}>
                <option value="en">English</option>
                <option value="nl">Nederlands</option>
              </select>
            </div>
          </div>
          <button onClick={updatePreferences} disabled={saving} className="btn-primary">
            <Save size={16} /> {saving ? t.saving : t.savePrefs}
          </button>
        </div>

        {/* Change Password */}
        <div className="profile-section">
          <h2 className="heading-2">{t.changePassword}</h2>
          <form onSubmit={changePassword} className="profile-form">
            <div className="form-group">
              <label className="body-small">{t.currentPassword}</label>
              <div className="input-with-icon"><Lock size={18} /><input type="password" value={passwordChange.current_password} onChange={(e) => setPasswordChange({ ...passwordChange, current_password: e.target.value })} className="form-input" required /></div>
            </div>
            <div className="form-group">
              <label className="body-small">{t.newPassword}</label>
              <div className="input-with-icon"><Lock size={18} /><input type="password" value={passwordChange.new_password} onChange={(e) => setPasswordChange({ ...passwordChange, new_password: e.target.value })} className="form-input" required minLength="6" /></div>
            </div>
            <div className="form-group">
              <label className="body-small">{t.confirmPassword}</label>
              <div className="input-with-icon"><Lock size={18} /><input type="password" value={passwordChange.confirm_password} onChange={(e) => setPasswordChange({ ...passwordChange, confirm_password: e.target.value })} className="form-input" required /></div>
            </div>
            <button type="submit" disabled={saving} className="btn-primary">
              <Lock size={16} /> {saving ? t.changing : t.changeBtn}
            </button>
          </form>
        </div>

        <div className="profile-section" data-testid="two-factor-settings">
          <h2 className="heading-2"><ShieldCheck size={20} style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />{t.twoFactorTitle}</h2>
          <p className="body-small text-secondary" style={{ marginBottom: '1.25rem' }}>{t.twoFactorDesc}</p>
          <form onSubmit={updateTwoFactor} className="profile-form">
            <div className="preference-item">
              <div className="preference-info"><ShieldCheck size={20} /><span>{twoFactorEnabled ? t.twoFactorEnabled : t.twoFactorDisabled}</span></div>
              <label className="toggle-switch">
                <input type="checkbox" checked={twoFactorEnabled} onChange={(e) => setTwoFactorEnabled(e.target.checked)} aria-label={t.twoFactorTitle} data-testid="two-factor-toggle" />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="form-group">
              <label className="body-small" htmlFor="two-factor-password">{t.twoFactorPassword}</label>
              <div className="input-with-icon"><Lock size={18} /><input id="two-factor-password" type="password" autoComplete="current-password" value={twoFactorPassword} onChange={(e) => setTwoFactorPassword(e.target.value)} className="form-input" required /></div>
            </div>
            <button type="submit" disabled={savingTwoFactor} className="btn-primary" data-testid="two-factor-save">
              <Save size={16} /> {savingTwoFactor ? t.saving : t.twoFactorSave}
            </button>
          </form>
        </div>

        {/* Delete Account */}
        <div className="profile-section" style={{ borderTop: '2px solid #fee2e2', paddingTop: '2rem' }}>
          <h2 className="heading-2" style={{ color: '#dc2626' }}>
            <Trash2 size={20} style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
            {t.deleteTitle}
          </h2>
          <p className="body-small text-secondary" style={{ marginBottom: '1rem' }}>{t.deleteDesc}</p>
          <button onClick={() => setShowDeleteModal(true)} className="btn-secondary" data-testid="delete-account-btn" style={{ color: '#dc2626', borderColor: '#fca5a5' }}>
            <Trash2 size={16} /> {t.deleteBtn}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} data-testid="delete-modal">
          <div style={{ background: '#0E1620', borderRadius: 16, padding: '2rem', maxWidth: 480, width: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
              <div style={{ background: '#16202C', borderRadius: '50%', padding: 10 }}>
                <AlertTriangle size={24} style={{ color: '#dc2626' }} />
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#F4F7FB' }}>{t.deleteConfirmTitle}</h3>
            </div>
            <p style={{ fontSize: '0.925rem', lineHeight: 1.6, color: '#555', marginBottom: '1.25rem' }} dangerouslySetInnerHTML={{ __html: t.deleteConfirmMsg }} />
            <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: '0.75rem' }} dangerouslySetInnerHTML={{ __html: t.deleteTypePrompt }} />
            <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder={t.deleteWord} className="form-input" data-testid="delete-confirm-input" style={{ marginBottom: '1.25rem' }} />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }} className="btn-secondary" data-testid="delete-cancel-btn">{t.cancel}</button>
              <button disabled={deleteConfirmText !== t.deleteWord || deleting} data-testid="delete-confirm-btn"
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await axios.delete(`${API}/auth/account`, { headers: { Authorization: `Bearer ${token}` } });
                    localStorage.clear();
                    window.location.href = '/';
                  } catch (err) { alert(err.response?.data?.detail || t.deleteError); setDeleting(false); }
                }}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none',
                  background: deleteConfirmText === t.deleteWord ? '#dc2626' : '#16202C',
                  color: deleteConfirmText === t.deleteWord ? '#fff' : '#999',
                  cursor: deleteConfirmText === t.deleteWord ? 'pointer' : 'not-allowed',
                  fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {deleting ? <Loader size={16} className="spin" /> : <Trash2 size={16} />}
                {deleting ? t.deleting : t.deleteForever}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
